import React, {useState, useRef, useEffect, useMemo} from 'react';
import {useDispatch} from 'react-redux';
import {map, filter} from 'lodash';
import {Card, Loader, Table, Dropdown, Checkbox} from 'semantic-ui-react';
import {Actions} from '../../../common/actions';
import {pageSizes} from '../../../common/constants/lists';
import TablePaginator from '../../../common/components/tablePaginator';
import StockDownload from './modal/stockDownload';
import TableDetailBody from './tableBody';

const {
  changeChassisMarked,
  changeOrdersMarked,
} = Actions;

const columnFilterStyle = {
  'position': 'relative',
  'display': 'inline-block',
};

const filterButtonStyle = {
  'background': 'none',
  'border': 'none',
  'cursor': 'pointer',
  'padding': '0 2px',
  'marginLeft': '4px',
  'fontSize': '12px',
  'verticalAlign': 'middle',
  'color': '#666',
  'lineHeight': 1,
};

const filterButtonActiveStyle = {
  ...filterButtonStyle,
  'color': '#2185d0',
  'fontWeight': 'bold',
};

const dropdownMenuStyle = {
  'position': 'absolute',
  'top': '100%',
  'left': '0',
  'zIndex': 9999,
  'background': '#fff',
  'border': '1px solid #ccc',
  'borderRadius': '4px',
  'boxShadow': '0 4px 16px rgba(0,0,0,0.18)',
  'minWidth': '200px',
  'maxHeight': '320px',
  'overflowY': 'auto',
  'padding': '6px 0',
};

const searchInputStyle = {
  'width': '100%',
  'padding': '6px 10px',
  'border': 'none',
  'borderBottom': '1px solid #eee',
  'outline': 'none',
  'fontSize': '14px',
  'boxSizing': 'border-box',
};

const menuItemStyle = {
  'padding': '5px 12px',
  'cursor': 'pointer',
  'fontSize': '14px',
  'display': 'flex',
  'alignItems': 'center',
  'gap': '8px',
  'whiteSpace': 'nowrap',
  'userSelect': 'none',
};

const menuItemHoverStyle = {
  ...menuItemStyle,
  'background': '#e8f0fe',
};

const actionRowStyle = {
  'display': 'flex',
  'gap': '8px',
  'padding': '6px 10px',
  'borderBottom': '1px solid #eee',
};

const actionBtnStyle = {
  'flex': 1,
  'padding': '4px 0',
  'fontSize': '13px',
  'cursor': 'pointer',
  'border': '1px solid #ccc',
  'borderRadius': '3px',
  'background': '#f5f5f5',
};

const actionBtnPrimaryStyle = {
  'flex': 1,
  'padding': '4px 0',
  'fontSize': '13px',
  'cursor': 'pointer',
  'background': '#2185d0',
  'color': '#fff',
  'border': 'none',
  'borderRadius': '3px',
};

const checkboxStyle = {
  'cursor': 'pointer',
  'width': '16px',
  'height': '16px',
};

const emptyStyle = {
  'padding': '8px 12px',
  'color': '#aaa',
  'fontSize': '13px',
};

const labelStyle = {
  'cursor': 'pointer',
  'userSelect': 'none',
};

const EMPTY_LABEL = '(Vacío)';

const toDisplayValue = (v) => {
  if (v === null || typeof v === 'undefined' || String(v).trim() === '') {
    return EMPTY_LABEL;
  }
  return String(v);
};

const getUniqueValues = (values) => (
  Array.from(new Set(values.map(toDisplayValue))).sort()
);

const getPageSlice = (list, page, pageSize) => {
  const start = page * pageSize;
  const end = start + pageSize;
  return list.slice(start, end);
};

// eslint-disable-next-line max-statements
const ColumnFilter = ({label, values, activeFilters, onFilterChange}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [hovered, setHovered] = useState(null);
  const [localSelected, setLocalSelected] = useState(activeFilters || []);
  const ref = useRef(null);

  useEffect(() => {
    setLocalSelected(activeFilters || []);
  }, [activeFilters]);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const uniqueValues = getUniqueValues(values);
  const filtered = uniqueValues.filter(
    (v) => v.toLowerCase().includes(search.toLowerCase())
  );
  const isActive = localSelected.length > 0 && localSelected.length < uniqueValues.length;

  const toggle = (val) => {
    setLocalSelected((prev) => (
      prev.includes(val) ? prev.filter((x) => x !== val) : [...prev, val]
    ));
  };

  const selectAll = () => setLocalSelected([...uniqueValues]);
  const clearAll = () => setLocalSelected([]);

  const apply = () => {
    const result = localSelected.length === uniqueValues.length ? [] : localSelected;
    onFilterChange(result);
    setOpen(false);
    setSearch('');
  };

  const toggleOpen = () => setOpen((o) => !o);
  const handleSearchChange = (e) => setSearch(e.target.value);
  const handleSearchClick = (e) => e.stopPropagation();

  return (
    <span style={columnFilterStyle} ref={ref}>
      <span style={labelStyle} onClick={toggleOpen}>
        {label}
      </span>
      <button
        style={isActive ? filterButtonActiveStyle : filterButtonStyle}
        onClick={toggleOpen}
        title="Filtrar"
      >
        {isActive ? '▼' : '⌄'}
      </button>
      {open && (
        <div style={dropdownMenuStyle}>
          <input
            autoFocus
            style={searchInputStyle}
            placeholder="Buscar..."
            value={search}
            onChange={handleSearchChange}
            onClick={handleSearchClick}
          />
          <div style={actionRowStyle}>
            <button style={actionBtnStyle} onClick={selectAll}>Todos</button>
            <button style={actionBtnStyle} onClick={clearAll}>Ninguno</button>
            <button style={actionBtnPrimaryStyle} onClick={apply}>Aceptar</button>
          </div>
          {filtered.map((val, i) => (
            <div
              key={i}
              style={hovered === i ? menuItemHoverStyle : menuItemStyle}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => toggle(val)}
            >
              <input
                type="checkbox"
                readOnly
                checked={localSelected.includes(val)}
                style={checkboxStyle}
              />
              <span>{val}</span>
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={emptyStyle}>Sin resultados</div>
          )}
        </div>
      )}
    </span>
  );
};

// eslint-disable-next-line max-statements
const Detail = ({type, accessoriesOrders, filtersState, ordersFiltered}) => {
  const [modalWasOpen, setModalWasOpen] = useState(false);
  const [orderSelected, setOrderSelected] = useState({});
  const [columnFilters, setColumnFilters] = useState({});
  const [localPage, setLocalPage] = useState(0);
  const [localPageSize, setLocalPageSize] = useState(20);
  const dispatch = useDispatch();

  const {
    loading,
    creating,
    downloading,
    list,
    chassisMarked,
    ordersMarked,
    orderStatusList,
  } = accessoriesOrders;

  const getColValues = (key) => (list || []).map((row) => row[key]);

  const filteredByColumns = useMemo(() => {
    let result = list || [];
    Object.entries(columnFilters).forEach(([col, selected]) => {
      if (selected && selected.length > 0) {
        result = result.filter((row) => selected.includes(toDisplayValue(row[col])));
      }
    });
    return result;
  }, [list, columnFilters]);

  const visiblePage = useMemo(
    () => getPageSlice(filteredByColumns, localPage, localPageSize),
    [filteredByColumns, localPage, localPageSize]
  );

  const handleColumnFilter = (col, selected) => {
    setColumnFilters((prev) => ({...prev, [col]: selected}));
    setLocalPage(0);
  };

  const clearAllFilters = () => {
    setColumnFilters({});
    setLocalPage(0);
  };

  const hasActiveFilters = Object.values(columnFilters).some(
    (sel) => sel && sel.length > 0
  );

  const handlePageChange = (value) => {
    setLocalPage(value);
  };

  const handlePageResize = (event, data) => {
    setLocalPageSize(data.value);
    setLocalPage(0);
  };

  const onOrderHeaderCheckboxChange = (event, value) => {
    if (value.checked) {
      const newOrders =
        type === 'accessories' ? filter(list, (elem) => elem.tasks) : list;
      dispatch(changeChassisMarked(map(newOrders, 'chassis')));

      const notNullOrders = filter(list, (elem) => elem.order !== null);
      dispatch(changeOrdersMarked(map(notNullOrders, 'order')));
    } else {
      dispatch(changeChassisMarked([]));
      dispatch(changeOrdersMarked([]));
    }
  };

  const openStockDownloadModal = (order) => {
    setModalWasOpen(true);
    setOrderSelected(order);
  };

  const closeStockDownloadModal = () => {
    setModalWasOpen(false);
    setOrderSelected({});
  };

  const fechaColLabel = type === 'pds' ? 'Fecha PDS' : 'Fecha Accesorios';

  if (loading || creating || downloading) {
    return (
      <Card raised fluid className='orders-detail'>
        <Loader active inline='centered'>
          Procesando...
        </Loader>
      </Card>
    );
  }

  return (
    <Card raised fluid className='orders-detail' style={{'overflow': 'auto'}}>
      {modalWasOpen && (
        <StockDownload
          modalWasOpen={modalWasOpen}
          accessoryOrder={orderSelected}
          type={type}
          onCloseModal={closeStockDownloadModal}
          filters={filtersState}
        />
      )}
      <div className='priorization-dates-table-wrapper'>
        {hasActiveFilters && (
          <div style={{'padding': '6px 10px', 'background': '#fff8e1', 'borderBottom': '1px solid #ffe082',
            'display': 'flex', 'alignItems': 'center', 'gap': '12px'}}>
            <span style={{'fontSize': '13px', 'color': '#795548'}}>
              Mostrando {filteredByColumns.length} de {(list || []).length} registros
            </span>
            <button
              onClick={clearAllFilters}
              style={{'fontSize': '13px', 'padding': '3px 10px', 'cursor': 'pointer',
                'border': '1px solid #e53935', 'borderRadius': '3px', 'background': '#fff', 'color': '#e53935'}}
            >
              ✕ Limpiar todos los filtros
            </button>
          </div>
        )}
        <Table className='interactive' striped>
          <Table.Header className='orders-table-header commercial-sticky-header'>
            <Table.Row>
              <Table.HeaderCell></Table.HeaderCell>
              {list.length !== 0 && (
                <Table.HeaderCell textAlign='center'>
                  <Checkbox
                    onChange={onOrderHeaderCheckboxChange}
                    disabled={loading}
                  />
                </Table.HeaderCell>
              )}
              {filtersState.invoiceType === 'S' ? <Table.HeaderCell></Table.HeaderCell> : ''}
              <Table.HeaderCell>
                <ColumnFilter
                  label="Concesionario"
                  values={getColValues('dealer')}
                  activeFilters={columnFilters.dealer || []}
                  onFilterChange={(sel) => handleColumnFilter('dealer', sel)}
                />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <ColumnFilter
                  label="Agencia"
                  values={getColValues('agency')}
                  activeFilters={columnFilters.agency || []}
                  onFilterChange={(sel) => handleColumnFilter('agency', sel)}
                />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <ColumnFilter
                  label="Ubicación"
                  values={getColValues('location')}
                  activeFilters={columnFilters.location || []}
                  onFilterChange={(sel) => handleColumnFilter('location', sel)}
                />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <ColumnFilter
                  label="Parking"
                  values={getColValues('parkingSlot')}
                  activeFilters={columnFilters.parkingSlot || []}
                  onFilterChange={(sel) => handleColumnFilter('parkingSlot', sel)}
                />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <ColumnFilter
                  label="Descripción"
                  values={getColValues('description')}
                  activeFilters={columnFilters.description || []}
                  onFilterChange={(sel) => handleColumnFilter('description', sel)}
                />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <ColumnFilter
                  label="Chasis"
                  values={getColValues('chassis')}
                  activeFilters={columnFilters.chassis || []}
                  onFilterChange={(sel) => handleColumnFilter('chassis', sel)}
                />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <ColumnFilter
                  label="Color"
                  values={getColValues('color')}
                  activeFilters={columnFilters.color || []}
                  onFilterChange={(sel) => handleColumnFilter('color', sel)}
                />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <ColumnFilter
                  label="Fecha MOV"
                  values={getColValues('movementDate')}
                  activeFilters={columnFilters.movementDate || []}
                  onFilterChange={(sel) => handleColumnFilter('movementDate', sel)}
                />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <ColumnFilter
                  label={fechaColLabel}
                  values={getColValues('date')}
                  activeFilters={columnFilters.date || []}
                  onFilterChange={(sel) => handleColumnFilter('date', sel)}
                />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <ColumnFilter
                  label="Combo"
                  values={getColValues('combo')}
                  activeFilters={columnFilters.combo || []}
                  onFilterChange={(sel) => handleColumnFilter('combo', sel)}
                />
              </Table.HeaderCell>
              <Table.HeaderCell textAlign='center'>
                <ColumnFilter
                  label="Tipo Facturación"
                  values={getColValues('anticipatedInvoice')}
                  activeFilters={columnFilters.anticipatedInvoice || []}
                  onFilterChange={(sel) => handleColumnFilter('anticipatedInvoice', sel)}
                />
              </Table.HeaderCell>
              <Table.HeaderCell textAlign='center'>
                <ColumnFilter
                  label="Nro. Orden"
                  values={getColValues('order')}
                  activeFilters={columnFilters.order || []}
                  onFilterChange={(sel) => handleColumnFilter('order', sel)}
                />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <ColumnFilter
                  label="Estado Orden"
                  values={getColValues('statusName')}
                  activeFilters={columnFilters.statusName || []}
                  onFilterChange={(sel) => handleColumnFilter('statusName', sel)}
                />
              </Table.HeaderCell>
              <Table.HeaderCell textAlign='center' colSpan='4'>Acciones</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <TableDetailBody
            openStockDownloadModal={openStockDownloadModal}
            type={type}
            list={visiblePage}
            chassisMarked={chassisMarked}
            ordersMarked={ordersMarked}
            ordersFiltered={visiblePage}
            orderStatusList={orderStatusList}
            filtersState={filtersState}
          />
          {filteredByColumns.length > localPageSize && (
            <Table.Footer>
              <Table.Row textAlign='center'>
                <Table.HeaderCell colSpan='100%'>
                  <div className='table-pagination'>
                    <span className='page-sizer'>
                      <strong>Items por página</strong>
                      <Dropdown
                        selection
                        upward
                        compact
                        value={localPageSize}
                        options={pageSizes}
                        onChange={handlePageResize}
                      />
                    </span>
                    <TablePaginator
                      itemsCount={filteredByColumns.length}
                      itemsPerPage={localPageSize}
                      selectedPage={localPage}
                      onOptionClick={handlePageChange}
                    />
                  </div>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Footer>
          )}
        </Table>
      </div>
    </Card>
  );
};

export default Detail;
